import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
  type CountryCode,
} from 'libphonenumber-js';

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'GB';

const LEGACY_PHONE_NUMBER_PATTERN = /^(?:\+?[1-9]\d{6,14}|0[1-9]\d{6,14})$/;

export type PhoneCountryOption = {
  label: string;
  value: CountryCode;
  callingCode: string;
};

export function getPhoneCountryOptions(): PhoneCountryOption[] {
  return getCountries()
    .map((countryCode) => {
      const callingCode = getCountryCallingCode(countryCode);

      return {
        label: `${countryCode} (+${callingCode})`,
        value: countryCode,
        callingCode,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function getPhoneDigits(value?: string | null): string {
  return (value ?? '').replace(/\D/g, '');
}

export function buildPhoneNumberFromParts(countryCode: CountryCode, phoneInput: string): string {
  const trimmedPhoneInput = phoneInput.trim();

  if (!trimmedPhoneInput) {
    return '';
  }

  const digits = getPhoneDigits(trimmedPhoneInput);
  const internationalParsedPhoneNumber = trimmedPhoneInput.startsWith('+')
    ? parsePhoneNumberFromString(trimmedPhoneInput)
    : parsePhoneNumberFromString(`+${digits}`);

  if (internationalParsedPhoneNumber?.isValid()) {
    return internationalParsedPhoneNumber.number;
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(trimmedPhoneInput, countryCode);

  if (parsedPhoneNumber != null) {
    return parsedPhoneNumber.number;
  }

  if (!digits) {
    return trimmedPhoneInput;
  }

  return `+${getCountryCallingCode(countryCode)}${digits}`;
}

export function getPhoneCountryFromValue(
  phoneNumber?: string | null,
  fallbackCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): CountryCode {
  const trimmedPhoneNumber = (phoneNumber ?? '').trim();

  if (!trimmedPhoneNumber) {
    return fallbackCountry;
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(trimmedPhoneNumber, fallbackCountry);

  return parsedPhoneNumber?.country ?? fallbackCountry;
}

export function getPhoneNationalValue(
  phoneNumber?: string | null,
  fallbackCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): string {
  const trimmedPhoneNumber = (phoneNumber ?? '').trim();

  if (!trimmedPhoneNumber) {
    return '';
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(trimmedPhoneNumber, fallbackCountry);

  return parsedPhoneNumber?.nationalNumber ?? getPhoneDigits(trimmedPhoneNumber);
}

export function getPhoneCountryFromInput(
  phoneInput: string,
  fallbackCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): CountryCode {
  const trimmedPhoneInput = phoneInput.trim();
  const digits = getPhoneDigits(trimmedPhoneInput);

  if (!digits) {
    return fallbackCountry;
  }

  const internationalParsedPhoneNumber = trimmedPhoneInput.startsWith('+')
    ? parsePhoneNumberFromString(trimmedPhoneInput)
    : parsePhoneNumberFromString(`+${digits}`);

  if (internationalParsedPhoneNumber?.isValid() && internationalParsedPhoneNumber.country != null) {
    return internationalParsedPhoneNumber.country;
  }

  return fallbackCountry;
}

export function getPhoneInputDisplayValue(phoneInput: string, countryCode: CountryCode): string {
  const trimmedPhoneInput = phoneInput.trim();

  if (!trimmedPhoneInput) {
    return phoneInput;
  }

  if (trimmedPhoneInput.startsWith('+')) {
    return getPhoneNationalValue(trimmedPhoneInput, countryCode);
  }

  const digits = getPhoneDigits(trimmedPhoneInput);
  const countryCallingCode = getCountryCallingCode(countryCode);
  const parsedPhoneNumber = parsePhoneNumberFromString(trimmedPhoneInput, countryCode);

  if (
    digits.startsWith(countryCallingCode) &&
    parsedPhoneNumber?.country === countryCode &&
    parsedPhoneNumber.isValid()
  ) {
    return parsedPhoneNumber.nationalNumber;
  }

  return phoneInput;
}

export function getPhoneNumberLengthIssue(
  phoneNumber?: string | null,
  fallbackCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): ReturnType<typeof validatePhoneNumberLength> {
  const trimmedPhoneNumber = (phoneNumber ?? '').trim();

  if (!trimmedPhoneNumber) {
    return 'TOO_SHORT';
  }

  const digits = getPhoneDigits(trimmedPhoneNumber);

  if (!digits) {
    return 'NOT_A_NUMBER';
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(trimmedPhoneNumber, fallbackCountry);
  const phoneNumberForValidation =
    parsedPhoneNumber?.number ?? (trimmedPhoneNumber.startsWith('+') ? trimmedPhoneNumber : `+${digits}`);

  return validatePhoneNumberLength(phoneNumberForValidation, parsedPhoneNumber?.country ?? fallbackCountry);
}

export function isValidSportsFiPhoneNumber(
  phoneNumber?: string | null,
  fallbackCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): boolean {
  const trimmedPhoneNumber = (phoneNumber ?? '').trim();
  const digits = getPhoneDigits(trimmedPhoneNumber);

  if (digits.length < 7 || digits.length > 15) {
    return false;
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(trimmedPhoneNumber, fallbackCountry);

  if (parsedPhoneNumber?.isValid()) {
    return true;
  }

  if (!trimmedPhoneNumber.startsWith('+')) {
    const internationalCandidate = `+${digits}`;

    if (isValidPhoneNumber(internationalCandidate)) {
      return true;
    }
  }

  return LEGACY_PHONE_NUMBER_PATTERN.test(trimmedPhoneNumber);
}

export function formatPhoneNumberForDisplay(phoneNumber?: string | null): string {
  const trimmedPhoneNumber = (phoneNumber ?? '').trim();

  if (!trimmedPhoneNumber) {
    return '';
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(trimmedPhoneNumber, DEFAULT_PHONE_COUNTRY);

  if (parsedPhoneNumber?.isValid()) {
    return parsedPhoneNumber.formatInternational();
  }

  return trimmedPhoneNumber;
}
